import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete, FormInputText, InputLabel } from '@hopskipdrive/react-components';

import { useReportsQuery } from '../../../hooks/useReportsQuery';

import { useTags } from './useTags';

const id = 'tags-filter';

export const Tags = () => {
  const { staticFilters } = useReportsQuery();
  const containsAllTags = /all_tags/;
  const { tags, isFetching, fetchTags, hasFetched } = useTags();
  const { control } = useFormContext();

  const tagOptions = useMemo(() => tags.map((tag) => ({ label: tag, value: tag })), [tags]);
  const combinedOptions = [...(staticFilters?.tags || []), ...tagOptions].map((option) => ({
    ...option,
    group: containsAllTags.test(option.value) ? 'predefined' : 'tags',
  }));

  return (
    <>
      <InputLabel htmlFor={id}>Tags</InputLabel>
      <Controller
        name="tags"
        control={control}
        defaultValue={[]}
        render={({ field: { value, onChange } }) => (
          <Autocomplete
            value={value}
            id={id}
            data-testid={id}
            options={combinedOptions}
            isLoading={isFetching}
            multiple
            getOptionDisabled={(option) => {
              const currentValues = value.map((option) => ({
                ...option,
                group: containsAllTags.test(option.value) ? 'predefined' : 'tags',
              }));

              return getOptionDisabled(option, { value: currentValues });
            }}
            autoHighlight
            isOptionEqualToValue={(option, value) => tagOptions.length === 0 || option.value === value.value}
            onOpen={() => !hasFetched && fetchTags()}
            renderInput={(params) => <FormInputText {...params} variant="outlined" />}
            onChange={(_, value) => onChange(value)}
          />
        )}
      />
    </>
  );
};

const getOptionDisabled = (option, { value = [] }: { value?: Array<{ value: string; group: string }> } = {}) => {
  const isApiOption = option.group === 'tags';
  const isPredefinedOption = option.group === 'predefined';

  if (isApiOption) {
    // Disable the entire 'predefined' group when any API option ('riders') is selected
    return value.some((selectedOption) => selectedOption.group === 'predefined');
  }
  if (isPredefinedOption) {
    // Disable all other predefined options when a predefined option is selected
    return value.some(
      (selectedOption) =>
        (selectedOption.group === 'predefined' && selectedOption.value !== option.value) ||
        selectedOption.group === 'tags'
    );
  }

  return false;
};
